// The rebooking funnel's step-by-step behaviour
// (resources/views/rebooking/start.blade.php).
//
// Three jobs, all of them V1 behaviours worth keeping:
//
//   1. Ask one question at a time. Each group appears once the one before it has an answer,
//      and the voucher field and submit appear once all three do.
//   2. Say immediately when an answer rules the booking out. Three answers can - a spa break,
//      a party larger than the journey handles, and a provider whose terms send amendments
//      through them. The point is not presentation: without it a customer has to answer
//      everything AND go and find a voucher number before being told the journey was never
//      going to work for them. A blocking answer therefore also hides everything downstream
//      of it, exactly as V1's own data-hide lists do.
//   3. Check the voucher's shape as it is typed - uppercasing it, marking the field valid or
//      not, saying so in words, and holding the submit until it matches. V1 does all four in
//      its own keystroke handler.
//
// REVEAL AND HIDE ONLY, over markup that is already correct. Every group and the continue
// region render VISIBLE and are hidden from here; every copy block renders HIDDEN and is
// revealed from here. So with this file absent the page is a plain three-question form that
// works, and the submit applies the same rules a moment later from the same
// App\Support\RebookingEligibility. That inversion is deliberate and is the same call
// checkin/conditionals.js makes for the same reason - V1 hides its steps in CSS, so with
// scripting off its funnel cannot be completed at all.
//
// Two things it deliberately does NOT do:
//
//   - It never decides WHETHER an answer blocks. The keys come from the server, so the
//     browser cannot disagree with the validator about the rules.
//   - It never gates the submit as the only defence. The button is put away because pressing
//     it is pointless, not because hiding it is what stops the submission - submitStart()
//     refuses the same answers whatever reaches it.

const FORM_SELECTOR = 'form.journey_form';
const STEP_SELECTOR = '[data-step-field]';
const NOTICE_SELECTOR = '[data-block-key]';
const CONTINUE_SELECTOR = '.rebooking_continue';
const VOUCHER_SELECTOR = 'input[name="voucher"]';
const VOUCHER_MESSAGE_SELECTOR = '.rebooking_voucher_message';

const VOUCHER_UNRECOGNISED = 'Sorry, we don\u2019t recognise your voucher. '
    + 'Please check you have entered it correctly.';

// Hidden regions also have their controls disabled. A hidden `required` radio still counts
// towards constraint validation, so the browser would refuse the submit while complaining
// about a control nobody can see or focus; and an unreached question should not submit a
// value at all. Previous answers survive being disabled, so going back and forth keeps them -
// as V1 does.
const CONTROL_SELECTOR = 'input, button, select, textarea';

function setRegionShown(region, shown)
{
    region.hidden = ! shown;

    // The region ITSELF where it is a control, not just its descendants: the submit button
    // carries .rebooking_continue directly, so querySelectorAll - which never matches the
    // element it is called on - would hide it and leave it live. Enter on a radio submits the
    // form in some browsers, and a live button would let that through to a server-side
    // rejection the customer has no way to understand.
    const controls = region.matches(CONTROL_SELECTOR)
        ? [region, ...region.querySelectorAll(CONTROL_SELECTOR)]
        : [...region.querySelectorAll(CONTROL_SELECTOR)];

    controls.forEach(control =>
    {
        control.disabled = ! shown;
    });
}

// What the customer has said so far, one entry per question in the order they are asked.
// data-blocks sits on the <label> because that is where <x-radio-box> puts a caller's extra
// attributes - the label is the whole control anyway.
function readSteps(form)
{
    return Array.from(form.querySelectorAll(STEP_SELECTOR)).map(region =>
    {
        const field = region.dataset.stepField;
        const checked = form.querySelector('[name="' + field + '"]:checked');

        return {
            region,
            answered: checked !== null,
            blockKey: checked?.closest('label')?.dataset.blocks ?? null,
        };
    });
}

// Valid, invalid, or nothing said yet - the three states V1's own handler has. `pattern` is
// read off the element rather than duplicated here, so the browser's native check, this one
// and the server's all come from the one expression RebookingRules builds.
function applyVoucherState(form)
{
    const voucher = form.querySelector(VOUCHER_SELECTOR);
    const message = form.querySelector(VOUCHER_MESSAGE_SELECTOR);

    if (! voucher)
    {
        return;
    }

    // Uppercased as it is typed, because the pattern accepts uppercase only. The server does
    // this too, so a code typed in lower case is accepted with this file absent.
    voucher.value = voucher.value.toUpperCase();

    const value = voucher.value.trim();
    const shaped = value !== '' && new RegExp('^(?:' + voucher.pattern + ')$').test(value);

    voucher.classList.toggle('valid', shaped);
    voucher.classList.toggle('error', value !== '' && ! shaped);

    // aria-invalid, not just a colour: the red border says "wrong" to sighted users only.
    if (value !== '' && ! shaped)
    {
        voucher.setAttribute('aria-invalid', 'true');
    }
    else
    {
        voucher.removeAttribute('aria-invalid');
    }

    if (message)
    {
        message.textContent = (value !== '' && ! shaped) ? VOUCHER_UNRECOGNISED : '';
    }

    form.querySelectorAll('button[type="submit"]').forEach(button =>
    {
        button.disabled = ! shaped;
    });
}

function apply(form)
{
    const steps = readSteps(form);

    // A question is reachable while every question before it has been answered and none of
    // those answers ruled the booking out. `blocked` latches: once one has, nothing after it
    // is offered.
    let reachable = true;
    let blockKey = null;

    steps.forEach(step =>
    {
        setRegionShown(step.region, reachable);

        if (! reachable)
        {
            return;
        }

        if (step.blockKey)
        {
            blockKey = step.blockKey;
            reachable = false;

            return;
        }

        reachable = step.answered;
    });

    // `reachable` survives the loop as "every question answered and none of them blocking",
    // which is exactly when there is any point asking for a voucher number.
    form.querySelectorAll(CONTINUE_SELECTOR).forEach(region =>
    {
        setRegionShown(region, reachable);
    });

    // Held until the code is voucher-SHAPED as well. Not the same thing as valid: whether it
    // exists, is this customer's and has not expired is settled by the provider's own API
    // later, and the server re-checks the same shape whatever reaches it.
    if (reachable)
    {
        applyVoucherState(form);
    }

    // role="status" makes each notice a live region, so revealing one announces itself. That
    // works only because all three are in the DOM from page load - a region inserted at the
    // moment it matters is not reliably announced.
    //
    // Searched from the form, where the notices sit: below the questions and above the
    // voucher entry, matching V1's own selection-grid order.
    form.querySelectorAll(NOTICE_SELECTOR).forEach(notice =>
    {
        notice.hidden = notice.dataset.blockKey !== blockKey;
    });
}

document.addEventListener('DOMContentLoaded', () =>
{
    const form = document.querySelector(FORM_SELECTOR);

    if (! form)
    {
        return;
    }

    form.addEventListener('change', () => apply(form));

    // 'input' as well as 'change': a text field only fires change on blur, and V1's voucher
    // box reacts on every keystroke.
    form.addEventListener('input', () => apply(form));

    // Run once on load, so a page returned carrying the customer's previous answers - a
    // validation failure, or the no-script blocked path - reopens at the same point a fresh
    // run of the same answers would.
    apply(form);
});
